import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { X, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, isOpen, setOpen, updateQty, removeItem, subtotal } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const onCheckout = async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
        })),
        origin_url: window.location.origin,
      };
      const { url } = await createCheckout(payload);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
      setCheckingOut(false);
    }
  };

  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 8.95;
  const total = subtotal + shipping;

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 bg-white border-l border-navy/10 flex flex-col [&>button]:hidden"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-navy/10 flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-display text-xl font-bold text-navy uppercase tracking-wider">
            Your Bag ({items.length})
          </SheetTitle>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="cart-close"
            className="p-1 hover:bg-sand transition-colors"
            aria-label="Close cart"
          >
            <X className="w-5 h-5 text-navy" strokeWidth={1.5} />
          </button>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center gap-4">
              <ShoppingBag className="w-12 h-12 text-navy/30" strokeWidth={1.2} />
              <p className="text-navy/60 text-sm">Your bag is empty. The tides are calling.</p>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-empty-cta"
                className="bg-navy text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:bg-seafoam hover:text-navy transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-navy/10">
              {items.map((item) => (
                <li key={item.key} className="flex gap-4 p-6" data-testid={`cart-item-${item.slug}`}>
                  <div className="w-20 h-24 bg-sand flex-shrink-0 overflow-hidden flex items-center justify-center p-1">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-display font-semibold text-sm text-navy leading-tight">{item.name}</h3>
                      <button
                        onClick={() => removeItem(item.key)}
                        data-testid={`cart-remove-${item.slug}`}
                        className="text-navy/40 hover:text-coral transition-colors"
                        aria-label="Remove"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-xs text-navy/50 uppercase tracking-widest mt-1">
                      {item.color}{item.size ? ` · ${item.size}` : ""}
                    </p>
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <div className="flex items-center border border-navy/15">
                        <button
                          onClick={() => updateQty(item.key, item.quantity - 1)}
                          className="px-2.5 py-1.5 hover:bg-sand"
                          data-testid={`cart-decrement-${item.slug}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" strokeWidth={2} />
                        </button>
                        <span className="px-3 text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(item.key, item.quantity + 1)}
                          className="px-2.5 py-1.5 hover:bg-sand"
                          data-testid={`cart-increment-${item.slug}`}
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </div>
                      <div className="text-sm font-bold text-navy">${(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-navy/10 p-6 space-y-4 bg-sand">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-navy/60">Subtotal</span>
                <span className="font-semibold text-navy" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy/60">Shipping</span>
                <span className="font-semibold text-navy">
                  {shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-navy/10 text-base">
                <span className="font-display font-bold text-navy uppercase tracking-wider">Total</span>
                <span className="font-display font-bold text-navy" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={onCheckout}
              disabled={checkingOut}
              data-testid="cart-checkout-button"
              className="w-full bg-navy text-white py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {checkingOut ? "Redirecting..." : "Secure Checkout"}
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            <p className="text-[10px] text-navy/40 text-center uppercase tracking-widest">
              Powered by Stripe · Secure encrypted payment
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
